import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

interface WalletData {
  wallet_balance: number;
  monthly_reward_total: number;
  annual_reward_total: number;
  total_points: number;
  transactions: Transaction[];
}

interface Transaction {
  amount: number;
  type: string;
  reason: string;
  timestamp: string;
  leadId?: string;
}

const WalletDashboard: React.FC = () => {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_URL}/admissions-game/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWalletData(response.data.data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading wallet...</div>;
  }

  if (!walletData) {
    return <div className="p-6">No wallet data available</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">My Wallet</h2>
      
      {/* Wallet Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="text-sm opacity-90 mb-1">Wallet Balance</div>
          <div className="text-3xl font-bold">₹{walletData.wallet_balance.toLocaleString()}</div>
        </div>
        
        <div className="bg-white border rounded-lg p-6">
          <div className="text-sm text-gray-600 mb-1">Monthly Earnings</div>
          <div className="text-2xl font-bold text-gray-900">₹{walletData.monthly_reward_total.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">Payout on 10th</div>
        </div>
        
        <div className="bg-white border rounded-lg p-6">
          <div className="text-sm text-gray-600 mb-1">Annual Earnings</div>
          <div className="text-2xl font-bold text-gray-900">₹{walletData.annual_reward_total.toLocaleString()}</div>
        </div>
        
        <div className="bg-white border rounded-lg p-6">
          <div className="text-sm text-gray-600 mb-1">Total Points</div>
          <div className="text-2xl font-bold text-blue-600">{walletData.total_points}</div>
        </div>
      </div>
      
      {/* Transaction History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Transaction History</h3>
        </div>
        
        <div className="divide-y">
          {walletData.transactions.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No transactions yet
            </div>
          ) : (
            walletData.transactions.map((transaction, index) => (
              <div key={index} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">
                    {transaction.reason.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(transaction.timestamp).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                  {transaction.leadId && (
                    <div className="text-xs text-gray-400">Lead: {transaction.leadId}</div>
                  )}
                </div>
                <div className={`text-lg font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {transaction.type === 'credit' ? '+' : '-'}₹{transaction.amount.toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Earning Info */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">💡 How to Earn</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Admission Confirmed: ₹500 per admission</li>
          <li>• Monthly payout on 10th of every month</li>
          <li>• Only staff referrals earn wallet credits</li>
        </ul>
      </div>
    </div>
  );
};

export default WalletDashboard;
